import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import GraphModal from "../graphModal/GraphModal";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterBadge from "../common/FilterBadge";
import "./subjectBarChart.scss";
import Skeleton from "@mui/material/Skeleton";
import { monthColorMap, StackedBarChartType } from "../../enum";
import CustomLegend from "./CustomLegend";

// Grade-based color mapping
const getGradeColor = (grade) => {
  switch (grade) {
    case "A1":
      return "#00C49F"; // Green
    case "A":
      return "#0088FE"; // Blue
    case "B":
      return "#FFBB28"; // Yellow
    case "C":
      return "#FF8042"; // Orange
    default:
      return "#FF0000"; // Red for Retry
  }
};

const getColor = (monthLabel) => {
  const month = monthLabel?.split(" ")[0];
  return monthColorMap[month] || "#cccccc"; // fallback color
};

// Define month order for chronological sorting
const monthOrder = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const SubjectBarChart = ({ title, data, loading, appliedFilters = [] }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasFilters = appliedFilters.length > 0 && !loading;
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  // ✅ Sort data by month order
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const monthA = a.month ? a.month.split(" ")[0] : "";
      const monthB = b.month ? b.month.split(" ")[0] : "";
      return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
    });
  }, [data]);

  const gradeLegend = [
    { grade: "A1", color: "#00C49F" },
    { grade: "A", color: "#0088FE" },
    { grade: "B", color: "#FFBB28" },
    { grade: "C", color: "#FF8042" },
    { grade: "Retry", color: "#FF0000" },
  ];

  // Dynamic font sizing based on name length
  const longestNameLength =
    sortedData.length > 0
      ? Math.max(...sortedData.map((d) => d.name.length))
      : 0;
  const xAxisFontSize = Math.max(10, 14 - Math.floor(longestNameLength / 6));

  // Tooltip Component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const studentData = payload[0].payload;
      return (
        <div className="subject-bar-tooltip">
          <p className="student-name" style={{ fontWeight: "bold" }}>
            {studentData.name}
          </p>
          <div className="subject-scores">
            <p>
              <span className="subject-label">Month:</span>
              <span
                className="subject-value"
                style={{ color: getColor(studentData.month) }}
              >
                {studentData.month || "N/A"}
              </span>
            </p>
            <p>
              <span className="subject-label">Percentage:</span>
              <span className="subject-value">
                {studentData.percentage
                  ? Math.round(studentData.percentage) + "%"
                  : "N/A"}
              </span>
            </p>
            <p>
              <span className="subject-label">Grade:</span>
              <span
                className="subject-value"
                style={{ color: getGradeColor(studentData.grade) }}
              >
                {studentData.grade || "N/A"}
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (
    <CustomLegend
      subjectKeys={gradeLegend}
      getColor={getColor}
      chartType={StackedBarChartType.SUBJECTRESULTS}
    />
  );

  return (
    <div
      className="subjectBarChartContainer"
      onMouseEnter={() => setShowFilters(true)}
      onMouseLeave={() => setShowFilters(false)}
    >
      <div className="chart-header">
        <h2 className="chartTitle">{title}</h2>
        {hasFilters && (
          <div className="filter-indicator">
            <FilterListIcon fontSize="small" />
          </div>
        )}
      </div>

      {hasFilters && showFilters && (
        <div className="filter-tooltip">
          <div className="filter-header">Applied Filters:</div>
          <div className="filter-badges">
            {appliedFilters.map((filter, index) => (
              <FilterBadge
                key={index}
                label={filter.label}
                value={filter.value}
              />
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={400}
          animation="wave"
          className="chart-skeleton"
        />
      ) : sortedData.length > 0 ? (
        <>
          {renderLegend}
          <div className="chart-wrapper" onClick={handleOpenModal}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={sortedData}
                margin={{ top: 0, right: 0, left: 0, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: xAxisFontSize, fontWeight: "bold" }}
                  tickMargin={5}
                  interval={0}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="percentage" name="Percentage" barSize={80}>
                  {sortedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getGradeColor(entry.grade)}
                    />
                  ))}
                  {/* ✅ Label on top of each bar */}
                  <LabelList
                    dataKey="percentage"
                    position="top"
                    formatter={(val) => `${Math.round(val)}`}
                    style={{ fontSize: 17, fontWeight: "bold", fill: "#333" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Modal Chart */}
          <GraphModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title={title}
            additionalContent={renderLegend}
          >
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={sortedData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: xAxisFontSize + 2, fontWeight: "bold" }}
                  tickMargin={5}
                  interval={0}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="percentage" name="Percentage" barSize={60}>
                  {sortedData.map((entry, index) => (
                    <Cell
                      key={`cell-modal-${index}`}
                      fill={getGradeColor(entry.grade)}
                    />
                  ))}
                  <LabelList
                    dataKey="percentage"
                    position="top"
                    formatter={(val) => `${Math.round(val)}`}
                    style={{ fontSize: 20, fontWeight: "bold", fill: "#333" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GraphModal>
        </>
      ) : (
        <div className="no-data">No data available for selected filters</div>
      )}
    </div>
  );
};

export default SubjectBarChart;
